import { useEffect, useState } from 'react';
import { fetchApi } from '../api/client';

interface WarmupResult {
  warm: boolean;
  mode: string;
  ms: number;
}

/**
 * JIT database warm-up. The SQL core db is serverless with 60-min auto-pause; the first
 * query after a pause pays a ~40s resume. Firing /warmup the moment the authenticated app
 * mounts makes that resume run concurrently with navigation/first render instead of
 * stalling the first data query the user actually waits on.
 *
 * Returns `waking: true` when the warm-up call has been in flight longer than
 * `thresholdMs` — the signal to show a "database waking up" notice — and flips back to
 * false when the database is awake (or the call fails; data queries have their own
 * retries, so the warm-up is strictly best-effort).
 */
export function useDbWarmup(thresholdMs = 4000) {
  const [waking, setWaking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!cancelled) setWaking(true);
    }, thresholdMs);

    fetchApi<WarmupResult>('/warmup')
      .catch(() => undefined) // best-effort: never surface warm-up errors to the user
      .finally(() => {
        clearTimeout(timer);
        if (!cancelled) setWaking(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [thresholdMs]);

  return { waking };
}
